import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api"; // not used, kept for parity
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScanLine } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

// ============ BARCODE SCANNER (camera via html5-qrcode) ============
// IMPORTANT: On Chrome Android 120+ and Samsung Internet, `getUserMedia` only
// triggers the native permission prompt when called *synchronously* inside a
// user-gesture handler (direct tap). If we fire it from a `useEffect` the
// prompt is silently blocked. So the modal now shows a big "Kamerayı Aç"
// button and only starts the camera inside that button's onClick handler.
export default function BarcodeScannerModal({ open, onClose, onScanned }) {
  const scannerRef = useRef(null);
  const streamRef = useRef(null);
  const cancelledRef = useRef(false);
  const [status, setStatus] = useState("Başlatmak için düğmeye tıkla");
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [manualCode, setManualCode] = useState("");

  // Clean up when modal closes
  useEffect(() => {
    if (open) {
      cancelledRef.current = false;
      return;
    }
    cancelledRef.current = true;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    // Stop + clear html5-qrcode, then wipe the region's innerHTML so React
    // can safely re-mount an empty region on next open without clashing
    // with leftover <video>/<canvas> nodes.
    const inst = scannerRef.current;
    scannerRef.current = null;
    if (inst) {
      Promise.resolve()
        .then(() => inst.stop().catch(() => {}))
        .then(() => { try { inst.clear(); } catch {} })
        .then(() => {
          try {
            const region = document.getElementById("barcode-reader-region");
            if (region) region.innerHTML = "";
          } catch {}
        });
    }
    setRunning(false);
    setStarting(false);
    setStatus("Başlatmak için düğmeye tıkla");
  }, [open]);

  // Short haptic + audio chirp on every successful decode so the cashier
  // feels the "scan locked in" instantly, even when the modal auto-closes.
  const feedbackSuccess = () => {
    try { navigator.vibrate?.(80); } catch {}
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1200, ctx.currentTime);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.16);
      osc.onended = () => { try { ctx.close(); } catch {} };
    } catch {}
  };

  const startCamera = async () => {
    if (starting || running) return;
    setStarting(true);
    setStatus("Kamera izni için ekrandaki tarayıcıya bak...");
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("Bu tarayıcı kamera API'si desteklemiyor. Chrome veya Safari kullan.");
        setStarting(false);
        return;
      }
      // Html5Qrcode.start() handles getUserMedia internally. Doing a probe
      // getUserMedia first + stopping its tracks caused Chrome Android to
      // lose the permission token on the second request → blank preview +
      // modal auto-closed because the dialog treated the resume-tap as an
      // "interact outside" event. Skip the probe; let the library ask.
      if (cancelledRef.current) return;

      setStatus("Kamera başlatılıyor...");
      const instance = new Html5Qrcode("barcode-reader-region", { verbose: false });
      scannerRef.current = instance;
      const config = { fps: 10, qrbox: { width: 260, height: 160 }, aspectRatio: 1.333 };
      const onDecoded = (text) => {
        if (!text) return;
        try { instance.stop().catch(() => {}); } catch {}
        feedbackSuccess();
        onScanned(text.trim());
      };
      try {
        await instance.start({ facingMode: "environment" }, config, onDecoded, () => {});
      } catch (envErr) {
        console.warn("environment camera failed, trying device id:", envErr);
        if (envErr?.name === "NotAllowedError" || /denied|permission/i.test(String(envErr))) {
          setStatus("⛔ Kamera izni verilmedi. Adres çubuğundaki 🔒 → Kamera → İzin Ver.");
          setStarting(false);
          return;
        }
        let cams = [];
        try { cams = await Html5Qrcode.getCameras(); } catch (listErr) {
          setStatus("Kamera listelenemedi: " + (listErr?.message || listErr?.name || "bilinmiyor"));
          setStarting(false);
          return;
        }
        if (!cams?.length) {
          setStatus("Kamera bulunamadı — barkodu elle gir");
          setStarting(false);
          return;
        }
        const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
        if (cancelledRef.current) return;
        try {
          await instance.start(back.id, config, onDecoded, () => {});
        } catch (startErr) {
          console.error("start by id failed:", startErr);
          setStatus("Kamera başlatılamadı: " + (startErr?.message || startErr?.name || "bilinmiyor"));
          setStarting(false);
          return;
        }
      }
      setStatus("📷 Barkodu kameraya doğru tutun");
      setRunning(true);
    } catch (e) {
      console.error(e);
      setStatus("Beklenmedik hata: " + (e.message || String(e)) + ". Elle gir.");
    } finally {
      setStarting(false);
    }
  };

  const nativeInputRef = useRef(null);
  const onNativePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus("Fotoğraf analiz ediliyor...");
    try {
      if (!("BarcodeDetector" in window)) {
        setStatus("Bu tarayıcı barkod analizini desteklemiyor. Elle gir.");
        return;
      }
      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "codabar", "itf"],
      });
      const bmp = await createImageBitmap(file);
      const codes = await detector.detect(bmp);
      if (!codes?.length) {
        setStatus("Barkod bulunamadı — başka açıdan fotoğraf çek veya elle gir");
        return;
      }
      feedbackSuccess();
      onScanned(codes[0].rawValue.trim());
    } catch (err) {
      console.error(err);
      setStatus("Analiz hatası: " + (err.message || err.name));
    } finally {
      e.target.value = "";
    }
  };

  const submitManual = () => {
    const code = manualCode.trim();
    if (code) {
      feedbackSuccess();
      onScanned(code);
      setManualCode("");
    }
  };

  const nativeSupported = typeof window !== "undefined" && "BarcodeDetector" in window;

  // Primary click: if the device supports BarcodeDetector (Chrome Android,
  // Safari 16.4+, modern Edge), open the native camera via <input capture>.
  // This is the path that actually works on most phones. Only fall back to
  // the html5-qrcode live preview when BarcodeDetector is unavailable.
  const onPrimaryClick = () => {
    if (nativeSupported && nativeInputRef.current) {
      nativeInputRef.current.click();
    } else {
      startCamera();
    }
  };

  // When the camera is active, block Shadcn Dialog's auto-close on
  // interact-outside / Escape — Chrome's permission popup, long-press
  // menus, and video controls can register as "outside clicks" and would
  // otherwise instantly close the whole scanner.
  const scannerActive = starting || running;
  const guardClose = (e) => { if (scannerActive) e.preventDefault(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="barcode-scanner-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md"
        onInteractOutside={guardClose}
        onEscapeKeyDown={guardClose}
        onPointerDownOutside={guardClose}
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> Barkod Oku
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Hidden native file input — tapped by the primary button when
              BarcodeDetector is available (Chrome Android, Safari 16.4+). */}
          <input
            ref={nativeInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onNativePhoto}
            data-testid="camera-native-input"
            className="hidden"
          />

          {/* IMPORTANT: #barcode-reader-region MUST stay empty from React's
              perspective. html5-qrcode mutates its innerHTML (injects a
              <video>). Any React child here would collide → React reconciler
              throws `removeChild: not a child of this node` and unmounts the
              whole admin page. So status + button are OVERLAID as siblings. */}
          <div className="relative w-full rounded-xl overflow-hidden bg-black min-h-[260px]">
            <div
              id="barcode-reader-region"
              data-testid="barcode-reader-region"
              className="w-full min-h-[260px]"
            />
            {!running && !starting && (
              <div className="absolute inset-0 flex items-center justify-center p-3 pointer-events-none">
                <Button
                  data-testid="camera-start-btn"
                  onClick={onPrimaryClick}
                  className="h-14 px-6 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold text-base pointer-events-auto"
                >
                  📷 Kamerayı Aç
                </Button>
              </div>
            )}
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-xs p-3 text-center pointer-events-none">
                <span data-testid="camera-starting">⏳ {status}</span>
              </div>
            )}
          </div>
          <p data-testid="camera-status" className="text-xs text-[#1B2A22]/70 text-center min-h-[18px]">
            {status}
          </p>

          {/* Secondary: html5-qrcode continuous live scanner. Only shown as a
              fallback button because on some Android browsers the <video>
              element renders blank even after permission is granted. */}
          {!running && !starting && (
            <div className="pt-2">
              <Button
                type="button"
                data-testid="camera-live-btn"
                variant="outline"
                onClick={startCamera}
                className="w-full h-11 rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
                title="Canlı tarama — kameraya tutun, otomatik okur"
              >
                🎥 Canlı Tarama Dene (yedek)
              </Button>
              <p className="text-[10px] text-[#1B2A22]/60 text-center mt-1">
                Fotoğraf çekmek yerine canlı video üzerinden okumak istersen.
              </p>
            </div>
          )}

          <div className="border-t border-[#E8E2D5] pt-3">
            <Label className="text-xs">Veya barkodu elle gir:</Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="barcode-manual-input"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="8690000000000"
                className="bg-white font-mono"
                onKeyDown={(e) => e.key === "Enter" && submitManual()}
              />
              <Button
                data-testid="barcode-manual-submit"
                onClick={submitManual}
                className="bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full"
              >
                Ara
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
